import type { Command, End, NewTask, Rule, When } from "../api/generated";
import type { TaskApi } from "../api/taskApi";
import { newId, parseProjectPath } from "./commands";
import type { DateAttribute, QuickAddDraft } from "./quickAdd";

/** What the current view contributes when the typed text leaves a field unsaid. */
export interface QuickAddDefaults {
  project?: string;
  dueText?: string;
  tag?: string;
}

export class QuickAddError extends Error {}

export async function commandForQuickAdd(
  api: Pick<TaskApi, "parseDate" | "parseCalendarDay" | "parseInterval">,
  draft: QuickAddDraft,
  defaults: QuickAddDefaults = {},
): Promise<Command> {
  if (draft.title.trim() === "") throw new QuickAddError("Type a title first.");
  const fields = await newTaskFields(api, draft, defaults);
  if (draft.recurrence === undefined) {
    return { Create: { id: newId(), fields } };
  }
  const rule = await ruleFor(api, draft.recurrence.mode, draft.recurrence.intervalText);
  return {
    CreateRecurring: {
      id: newId(),
      first_id: newId(),
      series: {
        rule,
        end: await endFor(api, draft),
        template: {
          title: fields.title,
          notes: fields.notes ?? "",
          priority: fields.priority ?? 0,
          project: fields.project ?? [],
          tags: fields.tags ?? [],
        },
      },
      first: fields,
    },
  };
}

async function newTaskFields(
  api: Pick<TaskApi, "parseDate">,
  draft: QuickAddDraft,
  defaults: QuickAddDefaults,
): Promise<NewTask> {
  const dueAttribute = draft.due ?? defaultDate(defaults.dueText);
  const projectText = draft.project ?? defaults.project ?? "";
  return {
    title: draft.title,
    notes: draft.notes ?? "",
    priority: draft.priority ?? 0,
    tags: defaults.tag !== undefined && !draft.tags.includes(defaults.tag) ? [...draft.tags, defaults.tag] : draft.tags,
    project: parseProjectPath(projectText),
    wait: await resolveDate(api, draft.wait),
    due: await resolveDate(api, dueAttribute),
  };
}

function defaultDate(text: string | undefined): DateAttribute | undefined {
  return text === undefined ? undefined : { kind: "text", text };
}

async function resolveDate(
  api: Pick<TaskApi, "parseDate">,
  attribute: DateAttribute | undefined,
): Promise<When | undefined> {
  if (attribute === undefined || attribute.kind === "clear") return undefined;
  return api.parseDate(attribute.text);
}

async function ruleFor(
  api: Pick<TaskApi, "parseInterval">,
  mode: "every" | "after",
  intervalText: string,
): Promise<Rule> {
  const interval = await api.parseInterval(intervalText);
  return mode === "after" ? { After: interval } : { Every: interval };
}

async function endFor(
  api: Pick<TaskApi, "parseCalendarDay">,
  draft: QuickAddDraft,
): Promise<End | undefined> {
  if (draft.count !== undefined) return { Count: draft.count };
  if (draft.until === undefined) return undefined;
  return { Until: await api.parseCalendarDay(draft.until) };
}
